import { Text, View, type StyleProp, type ViewStyle } from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';

import { Button } from './Button';
import { colors, typography } from '@/theme/tokens';

interface Props {
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  illustration?: 'cat' | 'shelf' | 'note' | 'alert';
  style?: StyleProp<ViewStyle>;
}

export function EmptyState({ title, message, actionLabel, onAction, illustration = 'cat', style }: Props) {
  return (
    <View
      style={[
        { alignItems: 'center', justifyContent: 'center', paddingVertical: 48, paddingHorizontal: 24, gap: 18 },
        style,
      ]}
    >
      <Illustration kind={illustration} />
      <View style={{ alignItems: 'center', gap: 6, maxWidth: 360 }}>
        <Text
          style={{
            fontFamily: typography.display.fontFamily,
            fontSize: 22,
            fontWeight: typography.weights.bold,
            color: colors.ink[900],
            letterSpacing: -0.4,
            textAlign: 'center',
          }}
        >
          {title}
        </Text>
        <Text
          style={{
            fontSize: 14,
            color: colors.ink[500],
            textAlign: 'center',
            lineHeight: 20,
          }}
        >
          {message}
        </Text>
      </View>
      {actionLabel && onAction ? <Button label={actionLabel} onPress={onAction} variant="primary" /> : null}
    </View>
  );
}

function Illustration({ kind }: { kind: NonNullable<Props['illustration']> }) {
  if (kind === 'cat') {
    // Gatito feliz con detalle, inspirado en el banner de CliniCAT.
    return (
      <Svg width={120} height={120} viewBox="0 0 120 120">
        <Circle cx={60} cy={60} r={54} fill={colors.magenta[100]} />
        {/* Orejas */}
        <Path d="M30 48 L40 22 L52 42 Z" fill={colors.magenta[800]} />
        <Path d="M90 48 L80 22 L68 42 Z" fill={colors.magenta[800]} />
        <Path d="M34 44 L40 30 L48 42 Z" fill={colors.magenta[400]} />
        <Path d="M86 44 L80 30 L72 42 Z" fill={colors.magenta[400]} />
        {/* Cabeza */}
        <Path d="M30 60 c0 -16 13 -28 30 -28 s30 12 30 28 v6 c0 16 -14 28 -30 28 s-30 -12 -30 -28 z" fill={colors.magenta[800]} />
        {/* Manchitas blancas (gato tuxedo) */}
        <Path d="M52 70 c-3 6 -3 14 0 20 c3 4 13 4 16 0 c3 -6 3 -14 0 -20 z" fill={colors.white} />
        {/* Ojos verdes con iluminación */}
        <Circle cx={50} cy={62} r={4} fill={colors.white} />
        <Circle cx={70} cy={62} r={4} fill={colors.white} />
        <Circle cx={50} cy={62} r={2.5} fill="#9DCD3A" />
        <Circle cx={70} cy={62} r={2.5} fill="#9DCD3A" />
        <Circle cx={51} cy={61} r={0.8} fill={colors.white} />
        <Circle cx={71} cy={61} r={0.8} fill={colors.white} />
        {/* Nariz */}
        <Path d="M58 74 L62 74 L60 77 Z" fill="#FF8FB3" />
        {/* Boca */}
        <Path d="M60 77 L60 80 M60 80 c-1 1.5 -3 1.5 -4 0 M60 80 c1 1.5 3 1.5 4 0" stroke={colors.magenta[900]} strokeWidth={1.2} fill="none" strokeLinecap="round" />
        {/* Bigotes */}
        <Path d="M44 76 L34 74 M44 78 L34 80 M76 76 L86 74 M76 78 L86 80" stroke={colors.white} strokeWidth={1} strokeLinecap="round" opacity={0.7} />
      </Svg>
    );
  }
  if (kind === 'shelf') {
    return (
      <Svg width={96} height={96} viewBox="0 0 120 120">
        <Circle cx={60} cy={60} r={52} fill={colors.crema[100]} />
        <Path d="M30 40h60v44H30z" fill={colors.white} stroke={colors.magenta[300]} strokeWidth={2} />
        <Path d="M30 60h60M50 40v44M70 40v44" stroke={colors.magenta[300]} strokeWidth={2} />
      </Svg>
    );
  }
  if (kind === 'note') {
    return (
      <Svg width={96} height={96} viewBox="0 0 120 120">
        <Circle cx={60} cy={60} r={52} fill={colors.crema[100]} />
        <Path d="M40 36h32a4 4 0 0 1 4 4v44a4 4 0 0 1-4 4H40a4 4 0 0 1-4-4V40a4 4 0 0 1 4-4z" fill={colors.white} stroke={colors.magenta[400]} strokeWidth={2} />
        <Path d="M44 50h24M44 60h24M44 70h16" stroke={colors.magenta[300]} strokeWidth={2} strokeLinecap="round" />
      </Svg>
    );
  }
  return (
    <Svg width={96} height={96} viewBox="0 0 120 120">
      <Circle cx={60} cy={60} r={52} fill={colors.warning[50]} />
      <Path d="M60 32l28 48H32z" fill={colors.warning[500]} />
      <Path d="M60 50v18" stroke={colors.white} strokeWidth={3} strokeLinecap="round" />
      <Circle cx={60} cy={74} r={2} fill={colors.white} />
    </Svg>
  );
}
